import { useEffect, useState } from "react";
import { parcoursApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Btn,
  Badge,
  Field,
  FieldLabel,
  Select,
  Textarea,
  EmptyState,
} from "@/components/DashboardUI";
import { Map, Plus, X, Trash2 } from "lucide-react";

const DEFAULT_FORM = {
  nom: "",
  type: "PICNIC_GAME",
  ville: "",
  description: "",
  prixPersonne: 9.5,
  maxJoueurs: 8,
  dureeMin: 150,
  nbEtapes: 6,
  nbCadenas: 25,
  nbSacs: 3,
  creneaux: ["10:00", "14:30"],
};

export default function ParcoursPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    try {
      setItems(await parcoursApi.list());
    } catch {
      toast.error("Erreur de chargement");
    }
  };

  useEffect(() => {
    reload();
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await parcoursApi.create(form);
      toast.success("Parcours créé");
      setShowForm(false);
      setForm(DEFAULT_FORM);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(id) {
    try {
      await parcoursApi.toggle(id);
      reload();
    } catch {
      toast.error("Erreur toggle");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Supprimer ce parcours et ses étapes/énigmes ?"))
      return;
    try {
      await parcoursApi.remove(id);
      toast.success("Supprimé");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="parcours-page">
      <PageHeader
        chapter="Chapitre pro · La carte"
        title="Parcours"
        subtitle={`${items.length} parcours créés`}
        testId="parcours-header"
        actions={
          <Btn
            variant={showForm ? "secondary" : "primary"}
            onClick={() => setShowForm(!showForm)}
            data-testid="btn-nouveau-parcours"
          >
            {showForm ? (
              <>
                <X className="w-4 h-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Nouveau parcours
              </>
            )}
          </Btn>
        }
      />

      {showForm && (
        <form onSubmit={handleCreate} data-testid="parcours-form">
          <Card>
            <h2 className="font-display text-xl text-parchemin mb-6">
              Créer un parcours
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Nom"
                value={form.nom}
                onChange={(v) => setForm({ ...form, nom: v })}
                required
                testid="form-nom"
              />
              <Field
                label="Ville"
                value={form.ville}
                onChange={(v) => setForm({ ...form, ville: v })}
                required
                testid="form-ville"
              />
              <div className="flex flex-col gap-2">
                <FieldLabel>Type</FieldLabel>
                <Select
                  data-testid="form-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  <option value="PICNIC_GAME" className="bg-noir">
                    🎒 Picnic-Game
                  </option>
                  <option value="PROMENADE" className="bg-noir">
                    🔍 Promenade
                  </option>
                </Select>
              </div>
              <Field
                label="Prix / personne (€)"
                type="number"
                step="0.50"
                value={form.prixPersonne}
                onChange={(v) =>
                  setForm({ ...form, prixPersonne: parseFloat(v) })
                }
                testid="form-prix"
              />
              <Field
                label="Max joueurs"
                type="number"
                value={form.maxJoueurs}
                onChange={(v) =>
                  setForm({ ...form, maxJoueurs: parseInt(v, 10) })
                }
                testid="form-maxJoueurs"
              />
              <Field
                label="Nb étapes"
                type="number"
                value={form.nbEtapes}
                onChange={(v) =>
                  setForm({ ...form, nbEtapes: parseInt(v, 10) })
                }
                testid="form-nbEtapes"
              />
              <Field
                label="Nb cadenas"
                type="number"
                value={form.nbCadenas}
                onChange={(v) =>
                  setForm({ ...form, nbCadenas: parseInt(v, 10) })
                }
                testid="form-nbCadenas"
              />
              <Field
                label="Durée (min)"
                type="number"
                value={form.dureeMin}
                onChange={(v) =>
                  setForm({ ...form, dureeMin: parseInt(v, 10) })
                }
                testid="form-duree"
              />
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <FieldLabel>Description</FieldLabel>
              <Textarea
                data-testid="form-description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="mt-6">
              <Btn
                type="submit"
                disabled={loading}
                data-testid="form-submit"
                size="lg"
              >
                {loading ? "Création…" : "Créer le parcours"}
              </Btn>
            </div>
          </Card>
        </form>
      )}

      {items.length === 0 && !showForm ? (
        <EmptyState
          icon={<Map className="w-6 h-6" />}
          title="Aucun parcours pour le moment."
          hint="Crée un premier parcours pour commencer."
          testId="parcours-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`parcours-card-${p.id}`}
              className="bg-noir-50/50 border border-or/15 hover:border-or/40 rounded-sm p-5 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg text-parchemin leading-tight">
                    {p.nom}
                  </h3>
                  <div className="text-xs text-parchemin/50 font-mono mt-1">
                    {p.ville} ·{" "}
                    {p.type === "PICNIC_GAME" ? "🎒 Picnic" : "🔍 Promenade"} ·{" "}
                    <span className="text-or">{p.prixPersonne}€/pers</span>
                  </div>
                </div>
                <Badge tone={p.actif ? "success" : "neutral"}>
                  {p.actif ? "Actif" : "Brouillon"}
                </Badge>
              </div>
              <div className="text-xs text-parchemin/60 font-mono mt-4 flex flex-wrap gap-x-3 gap-y-1">
                <span>{p._count?.etapes || 0} étapes</span>
                <span className="text-or/40">·</span>
                <span>{p.nbCadenas} cadenas</span>
                <span className="text-or/40">·</span>
                <span>{p._count?.reservations || 0} résa</span>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-or/10">
                <Btn
                  variant="secondary"
                  size="sm"
                  onClick={() => handleToggle(p.id)}
                  data-testid={`toggle-${p.id}`}
                >
                  {p.actif ? "Désactiver" : "Activer"}
                </Btn>
                <Btn
                  variant="dangerText"
                  size="sm"
                  onClick={() => handleDelete(p.id)}
                  data-testid={`delete-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                  Supprimer
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
